import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import dayjs from 'dayjs'
import { useEffect, useState } from 'react'

import { useSnackbar } from '@/hooks/useSnackbar'
import { useForceCloseLocalPMutation } from '@/store/api/purchaseContractApi'
import { getErrorMessage } from '@/utils/error'

import {
  compactDialogContentSx,
  dialogHeaderBtnSx,
  dialogTitleActionsSx,
  dialogTitleSx,
  dialogTitleTextSx,
  numSx,
  roSx,
} from '../purchase/dialogStyle'

interface Props {
  open: boolean
  contractNo: string
  pending: number | null
  canEdit: boolean
  onClose: () => void
  onSaved: () => void
}

/** Verbatim Access `btnSave_Click` guard message. */
const DATE_REQUIRED_MSG = 'Please choose Forced Close Date!'

const formatPending = (value: number | null): string => {
  if (value === null || value === undefined) return ''
  return Number.isInteger(value)
    ? value.toLocaleString('en-US')
    : value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

/**
 * Forced Close confirmation dialog — converts Access form
 * `frmProcurementQryLocalPForcedClose` (Procurement Purchase tab).
 *
 * CSS-grid field layout (explicit gridRow/gridColumn per field), mobile stack
 * to a single column. Date input uses DatePicker `DD-MMM-YYYY`. No fullScreen.
 */
export default function ProcurementForcedCloseDialog({
  open,
  contractNo,
  pending,
  canEdit,
  onClose,
  onSaved,
}: Props) {
  const snack = useSnackbar()
  const [forceClose, { isLoading }] = useForceCloseLocalPMutation()

  const [forcedCloseDate, setForcedCloseDate] = useState<string>('')
  const [dateError, setDateError] = useState<string>('')

  // Re-init each time the dialog opens — default Forced Close Date = today (Now()).
  useEffect(() => {
    if (!open) return
    setForcedCloseDate(dayjs().format('YYYY-MM-DD'))
    setDateError('')
  }, [open])

  const handleSave = async () => {
    if (!canEdit) return
    if (!forcedCloseDate || forcedCloseDate.trim() === '') {
      setDateError(DATE_REQUIRED_MSG)
      snack.warning(DATE_REQUIRED_MSG)
      return
    }
    try {
      await forceClose({ contractNo, forcedCloseDate }).unwrap()
      snack.success('Successfully Forced')
      onSaved()
      onClose()
    } catch (err) {
      snack.error(getErrorMessage(err, 'Failed to forced close this contract'))
    }
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth scroll="paper">
      <DialogTitle sx={dialogTitleSx}>
        <Typography
          component="span"
          noWrap
          title="Forced Close Confirmation"
          sx={dialogTitleTextSx}
        >
          Forced Close Confirmation
        </Typography>
        <Stack direction="row" spacing={0.5} sx={dialogTitleActionsSx}>
          <Button
            variant="contained"
            size="small"
            color="error"
            onClick={() => void handleSave()}
            disabled={isLoading || !canEdit}
            sx={dialogHeaderBtnSx}
          >
            {isLoading ? <CircularProgress size={14} color="inherit" /> : 'Forced Close'}
          </Button>
          <Button
            variant="outlined"
            size="small"
            onClick={onClose}
            disabled={isLoading}
            sx={dialogHeaderBtnSx}
          >
            Cancel
          </Button>
        </Stack>
      </DialogTitle>
      <Divider />
      <DialogContent dividers sx={compactDialogContentSx}>
        <Typography variant="body2" sx={{ mb: 2 }}>
          Do you want to forced close this contract with the following information?
        </Typography>

        <Box
          sx={{
            display: 'grid',
            gap: 1.5,
            gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
          }}
        >
          {/* Row 1: Contract No (readonly) */}
          <Box sx={{ gridRow: '1', gridColumn: { xs: '1', sm: '1 / span 2' } }}>
            <TextField
              label="Contract No"
              size="small"
              fullWidth
              value={contractNo ?? ''}
              InputProps={{ readOnly: true }}
              InputLabelProps={{ shrink: true }}
              sx={roSx}
            />
          </Box>

          {/* Row 2: Pending (readonly, numeric) */}
          <Box sx={{ gridRow: { xs: '2', sm: '2' }, gridColumn: '1' }}>
            <TextField
              label="Pending"
              size="small"
              fullWidth
              value={formatPending(pending)}
              InputProps={{ readOnly: true }}
              InputLabelProps={{ shrink: true }}
              sx={[numSx, roSx]}
            />
          </Box>

          {/* Row 2: Forced Close Date (required) */}
          <Box sx={{ gridRow: { xs: '3', sm: '2' }, gridColumn: { xs: '1', sm: '2' } }}>
            <DatePicker
              label="Forced Close Date *"
              format="DD-MMM-YYYY"
              value={forcedCloseDate ? dayjs(forcedCloseDate) : null}
              onChange={(value) => {
                const iso = value && value.isValid() ? value.format('YYYY-MM-DD') : ''
                setForcedCloseDate(iso)
                if (iso) setDateError('')
              }}
              slotProps={{
                textField: {
                  size: 'small',
                  fullWidth: true,
                  error: !!dateError,
                  helperText: dateError || undefined,
                },
              }}
            />
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  )
}
